import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Button, Chip, IconButton, useTheme } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { ColDef } from 'ag-grid-community';
import AgGridTable from '../../../components/table/AgGridTable';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../axios/axios';
import { toast } from 'react-toastify';
import { END_POINTS } from '../../../shared/endpoints';
import { SkeletonLoader } from '../../../modules/dashboard/shared';
import WhiteTooltip from '../../../components/WhiteTooltip';
import { useDispatch } from 'react-redux';
import { setConfigDiffData } from './ConfigDiff-slice';
import ViewSummaryDrawer from './ViewSummaryDrawer';
import moment from 'moment';


// Parse value that might be a string with + or - sign or a number
const parseDeltaValue = (value: number | string | undefined | null): number | null => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Remove + or - sign and parse
    const cleaned = value.trim().replace(/^[+-]/, '');
    const num = parseFloat(cleaned);
    if (!isNaN(num)) {
      // Check if original string had - sign
      if (value.trim().startsWith('-')) {
        return -num;
      }
      return num;
    }
  }
  return null;
};

// Get delta metadata (direction, color, display text)
const getDeltaMeta = (value: number | string | undefined | null, type?: 'addition' | 'deletion' | 'modified' | 'all') => {
  const num = parseDeltaValue(value);
  if (num === null || num === 0) {
    return { dir: 'none' as const, color: '#98A2B3', text: '0' };
  }

  // For specific types, use fixed colors and directions
  if (type === 'addition') {
    // Additions are always positive (green, up arrow)
    return { dir: 'up' as const, color: '#22C55E', text: Math.abs(num).toString().padStart(2, '0') };
  }
  if (type === 'deletion') {
    // Deletions are always negative (red, down arrow)
    return { dir: 'down' as const, color: '#F44336', text: Math.abs(num).toString().padStart(2, '0') };
  }

  // For 'all', 'modified', or unspecified: use sign to determine direction
  const dir = num > 0 ? 'up' : 'down';
  const color = dir === 'up' ? '#22C55E' : '#F44336';
  return { dir, color, text: Math.abs(num).toString().padStart(2, '0') };
};

// Render delta value with sign, arrow and color
const renderDelta = (value: number | string | undefined | null, opts?: { invertArrows?: boolean }) => {
  const meta = getDeltaMeta(value);

  if (meta.dir === 'none') {
    return (
      <span className="ag-ellipsis" style={{ color: meta.color, cursor: "pointer" }}>
        {meta.text}
      </span>
    );
  }

  // Determine which icon to show based on direction and optional arrow inversion
  let ShowUpIcon = meta.dir === 'up';
  let ShowDownIcon = meta.dir === 'down';

  if (opts?.invertArrows) {
    // Invert arrows: positive shows down arrow, negative shows up arrow
    if (meta.dir === 'up') {
      ShowUpIcon = false;
      ShowDownIcon = true;
    } else if (meta.dir === 'down') {
      ShowUpIcon = true;
      ShowDownIcon = false;
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: "pointer" }}>
      {ShowUpIcon && (
        <ArrowOutwardIcon
          sx={{
            fontSize: 14,
            color: meta.color
          }}
        />
      )}
      {ShowDownIcon && (
        <CallReceivedIcon
          sx={{
            fontSize: 14,
            color: meta.color
          }}
        />
      )}
      <span style={{ color: meta.color, cursor: "pointer" }}>{meta.text}</span>
    </Box>
  );
};

// Render breakdown item with arrow for tooltip
const renderBreakdownItem = (value: number | string | undefined | null, type?: 'addition' | 'deletion' | 'modified' | 'all') => {
  const meta = getDeltaMeta(value, type);

  if (meta.dir === 'none') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: "pointer" }}>
        <span>{meta.text}</span>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: meta.color, cursor: "pointer" }}>
      {meta.dir === 'up' && <ArrowOutwardIcon sx={{ fontSize: 12 }} />}
      {meta.dir === 'down' && <CallReceivedIcon sx={{ fontSize: 12 }} />}
      <span>{meta.text}</span>
    </Box>
  );
};

// API response type
type ApiHistoryRecord = {
  scan_id: string;
  asset_id: string;
  timestamp: string;
  asset_name: string;
  vendor: string;
  model: string;
  version: string;
  no_of_changes: number | string;
  change_breakdown: {
    all: number | string;
    additions: number | string;
    deletions: number | string;
    modified: number | string;
  };
  score_changes: number | string;
  misconfiguration_changes?: number | string;
  misconfigurations?: number | string;
};

// Transformed history data type
type HistoryDataRow = {
  id: number;
  scan_id: string;
  asset_id: string;
  timeStamp: string;
  assetName: string;
  vendor: string;
  model: string;
  version: string;
  numberOfChanges: {
    all: number;
    addition: number;
    deletion: number;
    modified: number;
  };
  no_of_changes: number | string;
  impactScore: number | string;
  misconfiguration: number | string;
  approvalDate: string;
  approvalBy: string;
  isAddedToCompare: boolean;
  file_id?: string;
  file_id_previous?: string;
  security_score?: number;
  previous_timeStamp?: string;
  previous_security_score?: number;
};

type AssetHistoryApiRecord = {
  scan_id?: string;
  id?: string;
  timestamp?: string;
  no_of_changes?: number;
  score_changes?: number;
  misconfiguration_changes?: number;
  cve_changes?: number;
  status?: string;
  file_id?: string;
  date_added?: any;
  previous_file?: any;
  security_score?: any;
  change_breakdown?: {
    all?: number;
    additions?: number;
    deletions?: number;
    modified?: number;
  };
};

type HistoryTableRow = {
  asset_id: string;
  scan_id: string;
  timeStamp: string;
  numberOfChanges: {
    all: number | string;
    addition: number | string;
    deletion: number | string;
    modified: number | string;
  };
  no_of_changes: {
    all: number | string;
    addition: number | string;
    deletion: number | string;
    modified: number | string;
  };
  vendor: number | string;
  misconfiguration_changes: number | string;
  cve_changes: number | string;
  status?: string;
  isAddedToCompare?: boolean;
  raw?: AssetHistoryApiRecord | null;

};

export interface CVEData {
  cve_id: string;
  asset_count: number;
  description: string;
  cisas_kev: string;
  epss: string;
  version: string;
  score: string;
  vector_string: string;
  severity: string;
  cvss_data: Array<{
    score: number;
    vector_string: string;
    severity: string;
    version: string;
  }>;
  cve_issued_date: string;
  cve_last_updated: string;
  summaryData?: {
    cve_summary: string;
    cve_details: {
      vulnerability_name: string | null;
      date_added: string | null;
      due_date: string | null;
      required_action: string | null;
    };
    nvd_pd: string;
    nvd_lmd: string;
    source: string;
    severity: string;
    vulnerability_details: {
      authority: string[];
      attack_vector: string;
      confedentiality_impact: string;
      attack_complexity: string;
      integrity_impact: string;
      privileges_required: string;
      availability_impact: string;
      user_interaction: string;
      scope: string;
      cvss_score: number;
      cisco_data_flag: boolean;
      cisco_data: {
        attack_vector: string;
        confedentiality_impact: string;
        attack_complexity: string;
        integrity_impact: string;
        privileges_required: string;
        availability_impact: string;
        user_interaction: string;
        scope: string;
        cvss_score: number;
        severity: string;
      };
      epss_details: {
        pea: number;
        pov: number;
      };
    };
    reference: Array<{
      hyperlinks: {
        label: string;
        link: string;
        resources: string;
      };
    }>;
    weakness_enumeration: Array<{
      cwe_id: string;
      cwe_name: string;
      source: string;
    }>;
    known_affected_sc: Array<{
      cpe: string;
      to?: string;
      to_include?: boolean;
    }>;
    cisa_data_available: boolean;
    epss_data_available: boolean;
  };
}

export interface SummaryData {
  configurationChanges: {
    all_changes: number;
    additions: number;
    deletions: number;
    modified: number;
  };
  vulnerabilityChanges: {
    total_changes: number;
    added: number;
    removed: number;
  };
  cveChanges: {
    total_count: number;
    added: number;
    removed: number;
  };
}

type HistoryTableProps = {
  height?: number | string;
  device_id?: string;
  vendor?: string | null;
};

const HistoryTable: React.FC<HistoryTableProps> = ({ height, device_id, vendor }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const gridRef = useRef<any>(null);
  const [historyData, setHistoryData] = useState<HistoryDataRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const effectivePageSize = 20;
  const [isSummaryDrawerOpen, setIsSummaryDrawerOpen] = useState(false);
  const [summaryDataLoading, setSummaryDataLoading] = useState(false);
  const [assetSummary, setAssetSummary] = useState<any>(null);

  const fetchScanChangesData = async (asset_id: string, scan_id: string) => {
    try {
      const response = await axiosInstance.get(
        END_POINTS.ASSET_VIEWSUMMARY,
        { params: { asset_id, scan_id } }
      );

      if (response.data?.status === 200) {
        const scan = response.data.data?.scans?.[0];

        if (!scan) return;

        setAssetSummary({
          configuration_changes: scan.configuration_changes,
          vulnerability_summary: scan.vulnerability_summary,
          vulnerability_changes: scan.vulnerability_changes || [],
          cve_summary: scan.cve_summary,
          cve_changes: scan.cve_changes || [],
          is_first_scan: scan.is_first_scan,
        });
      }
    } catch (error) {
      console.error("Error fetching ASSET_VIEWSUMMARY:", error);
    }
  };

  const toSignedNumber = (val: any): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const num = Number(val.replace("+", ""));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  const summaryData = useMemo(() => {
    const config = assetSummary?.configuration_changes ?? {};
    const vuln = assetSummary?.vulnerability_summary ?? {};
    const cve = assetSummary?.cve_summary ?? {};

    return {
      is_first_scan: assetSummary?.is_first_scan,
      configurationChanges: {
        all_changes: toSignedNumber(config.all_changes),
        additions: toSignedNumber(config.additions),
        deletions: toSignedNumber(config.deletions),
        modified: toSignedNumber(config.modified),
      },

      vulnerabilityChanges: {
        total_changes: toSignedNumber(vuln.total_changes),
        added: toSignedNumber(vuln.added),
        removed: toSignedNumber(vuln.removed),
      },

      cveChanges: {
        total_count: toSignedNumber(cve.total_count),
        added: toSignedNumber(cve.added),
        removed: toSignedNumber(cve.removed),
      },

      misconfigurationChanges: assetSummary?.vulnerability_changes || [],
      cves: assetSummary?.cve_changes || [],
    };
  }, [assetSummary]);


  const parseToNumber = (value: number | string | undefined | null) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      // Handle strings with + or - prefix like "+38", "-37", "+1"
      const cleanedValue = value.replace(/^\+/, ''); // Remove leading + if present
      const num = Number(cleanedValue);
      return isNaN(num) ? null : num;
    }
    return null;
  };

  const handleOpenSummary = async (row?: HistoryTableRow) => {
    setIsSummaryDrawerOpen(true);

    if (!row || !device_id) {
      setAssetSummary({
        total: 0,
        additions: 0,
        deletions: 0,
        modified: 0,
      });
      return;
    }
    const asset_id = row.asset_id;
    const scan_id = row.scan_id;
    // const historyId = String(row.id);

    // Derive configuration diff stats
    const changes = (row.no_of_changes || row.numberOfChanges) as any;
    const additions = parseToNumber(changes?.addition) || 0;
    const deletions = parseToNumber(changes?.deletion) || 0;
    const modified = parseToNumber(changes?.modified) || 0;
    const total =
      parseToNumber(changes?.all) || additions + deletions + modified;

    setAssetSummary({
      total: Math.abs(total),
      additions: Math.abs(additions),
      deletions: Math.abs(deletions),
      modified: Math.abs(modified),
    });
    setSummaryDataLoading(true);
    // Fetch summary data
    try {
      await Promise.all([
        fetchScanChangesData(asset_id, scan_id),
      ]);
    } catch (error) {
      console.error("Error loading summary data:", error);
    } finally {
      setSummaryDataLoading(false);
    }
  };

  const handleCloseSummary = () => {
    setIsSummaryDrawerOpen(false);
  };

  // Fetch history data from API
  useEffect(() => {
    let ignore = false;

    const fetchHistoryData = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          END_POINTS.CONFIG_DIFF_EVENTS_History
        );

        if (ignore) {
          return;
        }

        const apiData = response?.data?.data;
        if (apiData && Array.isArray(apiData.scans)) {
          const transformedData: HistoryDataRow[] = apiData.scans.map(
            (record: ApiHistoryRecord, idx: number) => {
              const parseAndRound = (value: number | string | undefined | null): number => {
                const parsed = parseDeltaValue(value);
                return parsed !== null ? Math.round(parsed) : 0;
              };

              const allChanges = record.change_breakdown?.all ?? record.no_of_changes ?? 0;
              const noOfChanges = record.no_of_changes ?? 0;

              return {
                id: idx + 1,
                scan_id: record.scan_id,
                asset_id: record.asset_id,
                timeStamp: record.timestamp,
                assetName: record.asset_name,
                vendor: record.vendor,
                model: record.model,
                version: record.version,

                numberOfChanges: {
                  all: parseAndRound(allChanges),
                  addition: parseAndRound(record.change_breakdown?.additions),
                  deletion: parseAndRound(record.change_breakdown?.deletions),
                  modified: parseAndRound(record.change_breakdown?.modified),
                },

                no_of_changes: parseAndRound(noOfChanges),
                impactScore: parseAndRound(record.score_changes),
                misconfiguration: parseAndRound(record.misconfiguration_changes),

                approvalDate: "-",
                approvalBy: "-",
                isAddedToCompare: false,

                file_id: `file_${idx + 1}_${Date.now()}`,
                file_id_previous: idx > 0 ? `file_${idx}_${Date.now()}` : undefined,
                security_score: Math.floor(Math.random() * 100),
                previous_timeStamp: idx > 0 && apiData.scans[idx - 1]
                  ? apiData.scans[idx - 1].timestamp
                  : undefined,
                previous_security_score: idx > 0 ? Math.floor(Math.random() * 100) : undefined,
              };
            }
          );
          setHistoryData(transformedData);
          setSelectedRows([]);
        } else {
          setHistoryData([]);
          setSelectedRows([]);
        }
      } catch (error: any) {
        if (!ignore) {
          const errorMessage = error?.response?.data?.message || 'Failed to load history data';
          toast.error(errorMessage);
          setHistoryData([]);
          setSelectedRows([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchHistoryData();

    return () => {
      ignore = true;
    };
  }, [device_id]);

  const handleAddToCompare = (rowId: number) => {
    setSelectedRows((prev) => {
      if (prev.includes(rowId)) {
        // Remove if already selected
        return prev.filter((id) => id !== rowId);
      } else {
        // Add only if less than 2 are selected
        if (prev.length < 2) {
          return [...prev, rowId];
        }
        return prev;
      }
    });
  };

  const handleShowDiff = (rowId: number) => {
    const rowData = historyData.find((item) => item.id === rowId);
    if (rowData) {
      // Dispatch row data to Redux store
      dispatch(setConfigDiffData({
        rowData: {
          ...rowData,
        },
        click: "event-config-diff",
        source: "configdiff-sidebar",
      }));
      // Navigate to ShowConfigDiffPage
      navigate('/on-premises/configdiff/show-diff');
    }
  };

  const handleCompareClick = () => {
    if (selectedRows.length !== 2) {
      return;
    }

    const selectedRowData = selectedRows
      .map((rowId) => historyData.find((item) => item.id === rowId))
      .filter((row): row is HistoryDataRow => Boolean(row));

    if (selectedRowData.length !== 2) {
      return;
    }

    const selectedRowsPayload = selectedRowData.map((row) => ({
      ...row,
      device_id: device_id || (row as any).device_id,
    }));

    console.log("Compare navigate payload:", selectedRowsPayload);

    // Dispatch comparison data to Redux store
    dispatch(setConfigDiffData({
      selectedRows: selectedRowsPayload,
      device_id: device_id || null,
      click: "event-config-diff",
      source: "configdiff-sidebar",
    }));

    // console.log("Compare navigate payload:", payload);

    navigate("/on-premises/configdiff/show-diff");
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'timeStamp',
        headerName: 'Time Stamp',
        flex: 1,
        minWidth: 175,
        filter: false,
        sortable: false,
        cellRenderer: (params: any) => {
          const formattedTime = params.value
            ? moment(params.value).format("DD-MMM-YYYY HH:mm:ss")
            : "-";

          return (
            <WhiteTooltip title={formattedTime} arrow>
              <span className="ag-ellipsis" style={{ color: "#667085", cursor: "pointer" }}>
                {formattedTime}
              </span>
            </WhiteTooltip>
          );
        },
      },
      {
        field: 'assetName',
        headerName: 'Asset Name',
        flex: 1,
        minWidth: 150,
        filter: false,
        sortable: true,
        cellRenderer: (params: any) => (
          <WhiteTooltip title={params.value} arrow>
            <span className="ag-ellipsis" style={{ cursor: "pointer" }}>{params.value || '-'}</span>
          </WhiteTooltip>
        ),
      },
      {
        field: 'vendor',
        headerName: 'Vendor',
        width: 80,
        minWidth: 80,
        filter: false,
        sortable: true,
        cellRenderer: (params: any) => (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: '#E3F2FD',
              color: '#1976D2',
              fontSize: '11px',
              height: '24px',
              cursor: 'pointer',
            }}
          />
        ),
      },
      {
        field: 'model',
        headerName: 'OS',
        width: 80,
        minWidth: 120,
        filter: false,
        sortable: true,
        cellRenderer: (params: any) => (
          <WhiteTooltip title={params.value} arrow>
            <span className="ag-ellipsis" style={{ cursor: "pointer" }}>{params.value || '-'}</span>
          </WhiteTooltip>
        ),
      },
      {
        field: 'version',
        headerName: 'Version',
        width: 100,
        minWidth: 100,
        filter: false,
        sortable: true,
        cellRenderer: (params: any) => (
          <WhiteTooltip title={params.value} arrow>
            <span className="ag-ellipsis" style={{ cursor: "pointer" }}>{params.value || '-'}</span>
          </WhiteTooltip>
        ),
      },
      {
        field: 'numberOfChanges',
        headerName: 'No. of Changes',
        width: 150,
        minWidth: 150,
        filter: false,
        sortable: true,
        cellRenderer: (params: any) => {
          const changes = params.value;
          const noOfChanges = params.data?.no_of_changes ?? changes?.all ?? 0;
          return (
            <WhiteTooltip
              title={
                <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: isDarkMode ? "#000000" : "#000000" }}>
                    <span className="ag-ellipsis">All:</span>
                    {renderBreakdownItem(changes.all, 'all')}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: isDarkMode ? "#000000" : "#000000" }}>
                    <span className="ag-ellipsis">Addition:</span>
                    {renderBreakdownItem(changes.addition, 'addition')}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: isDarkMode ? "#000000" : "#000000" }}>
                    <span className="ag-ellipsis">Deletion:</span>
                    {renderBreakdownItem(changes.deletion, 'deletion')}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: isDarkMode ? "#000000" : "#000000" }}>
                    <span className="ag-ellipsis">Modified:</span>
                    {renderBreakdownItem(changes.modified, 'modified')}
                  </Box>
                </Box>
              }
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'common.white',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 3,
                  },
                },
                arrow: {
                  sx: { color: 'common.white' },
                },
              }}
            >
              <span className="ag-ellipsis" style={{ color: "#667085", cursor: "pointer" }}>
                {Math.abs(Math.round(parseDeltaValue(noOfChanges) ?? 0))}
              </span>
            </WhiteTooltip>
          );
        },
      },
      // {
      //   field: 'impactScore',
      //   headerName: 'Score Changes',
      //   width: 120,
      //   minWidth: 120,
      //   filter: false,
      //   sortable: true,
      //   cellRenderer: (params: any) => (
      //     <WhiteTooltip title={params.value} arrow>
      //       <span className="ag-ellipsis" style={{ cursor: "default" }}>
      //         {renderDelta(params.value)}
      //       </span>
      //     </WhiteTooltip>
      //   ),
      // },
      {
        field: 'misconfiguration',
        headerName: 'Misconfiguration',
        minWidth: 150,
        filter: false,
        sortable: true,
        cellRenderer: (params: any) => (
          // <WhiteTooltip title={params.value} arrow>
          <span className="ag-ellipsis" style={{ cursor: "pointer" }}>
            {renderDelta(params.value, { invertArrows: true })}
          </span>
          // </WhiteTooltip>
        ),
      },
      // {
      //   field: 'approvalDate',
      //   headerName: 'Approval Date',
      //   width: 140,
      //   filter: false,
      //   sortable: true,
      //   cellRenderer: (params: any) => (
      //     <Tooltip title={params.value} arrow>
      //       <span className="ag-ellipsis">{params.value || '-'}</span>
      //     </Tooltip>
      //   ),
      // },
      // {
      //   field: 'approvalBy',
      //   headerName: 'Approval by',
      //   width: 130,
      //   filter: false,
      //   sortable: true,
      //   cellRenderer: (params: any) => (
      //     <Tooltip title={params.value} arrow>
      //       <span className="ag-ellipsis">{params.value || '-'}</span>
      //     </Tooltip>
      //   ),
      // },
      {
        field: 'showDiff',
        headerName: 'Show Diff',
        minWidth: 120,
        width: 120,
        filter: false,
        sortable: false,
        cellRenderer: (params: any) => {
          const vendorStr = String(params.data?.vendor || vendor || '').trim().toLowerCase();
          const blockedVendors = [
            "ubuntu", "debian", "redhat", "microsoft", "huawei", "mikrotik", "edgecore",
            "apple", "macos", "mac",  "mac os",  "mac os x"
          ];
          const isEyeDisabled = Boolean(
            vendorStr && blockedVendors.some((b) => vendorStr === b || vendorStr.startsWith(b) || vendorStr.includes(b))
          );
          const eyeTooltip = isEyeDisabled ? `Diff not available for ${params.data?.vendor || vendor || 'this device'}` : undefined;

          const eyeButton = (
            <IconButton
              size="small"
              onClick={() => handleShowDiff(params.data.id)}
              disabled={isEyeDisabled}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: isEyeDisabled ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                },
                '&.Mui-disabled': {
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          );

          return eyeTooltip ? (
            <WhiteTooltip title={eyeTooltip} arrow>
              <span>{eyeButton}</span>
            </WhiteTooltip>
          ) : (
            eyeButton
          );
        },
      },
      {
        field: 'addToCompare',
        headerName: 'Add to Compare',
        minWidth: 150,
        width: 150,
        filter: false,
        sortable: false,
        cellRenderer: (params: any) => {
          const isSelected = selectedRows.includes(params.data.id);
          const canCompare = historyData.length > 1;

          // Vendor blacklist
          const vendorStr = String(params.data?.vendor || vendor || '').trim().toLowerCase();
          const blockedVendors = [
            "ubuntu", "debian", "redhat", "microsoft", "huawei", "mikrotik", "edgecore",
            "apple", "macos", "mac", "mac os", "mac os x"
          ];
          const isVendorBlocked = Boolean(
            vendorStr && blockedVendors.some((b) => vendorStr === b || vendorStr.startsWith(b) || vendorStr.includes(b))
          );

          // Disable if history has <= 1 record
          const isHistoryInsufficient = !canCompare;

          // Max 2 selection rule
          const isMaxSelected = !isSelected && selectedRows.length === 2;

          // Vendor + model mismatch rule
          let isMismatch = false;
          if (!isVendorBlocked && selectedRows.length === 1 && !isSelected) {
            const firstRow = historyData.find(r => r.id === selectedRows[0]);
            if (firstRow) {
              const sameVendor =
                String(firstRow.vendor).toLowerCase() === vendorStr;
              const sameModel =
                String(firstRow.model).toLowerCase() ===
                String(params.data.model).toLowerCase();

              if (!sameVendor || !sameModel) {
                isMismatch = true;
              }
            }
          }

          const isDisabled =
            isHistoryInsufficient ||
            isVendorBlocked ||
            isMaxSelected ||
            isMismatch;

          let tooltipText: string | undefined;
          if (isHistoryInsufficient) tooltipText = 'At least 2 history records are required to compare';
          else if (isVendorBlocked) tooltipText = `Compare not available for ${vendorStr}`;
          else if (isMaxSelected) tooltipText = 'Maximum 2 selections allowed';
          else if (isMismatch) tooltipText = 'Only rows with same vendor and model can be compared';

          const compareButton = (
            <IconButton
              size="small"
              onClick={() => handleAddToCompare(params.data.id)}
              disabled={isDisabled}
              sx={{
                color: isSelected ? theme.palette.primary.main : theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: isDisabled ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                },
                '&.Mui-disabled': {
                  color: theme.palette.action.disabled,
                },
              }}
            >
              {isSelected ? (
                <RemoveCircleIcon fontSize="small" />
              ) : (
                <AddCircleOutlineIcon fontSize="small" />
              )}
            </IconButton>
          );

          return tooltipText ? (
            <WhiteTooltip title={tooltipText} arrow>
              <span>{compareButton}</span>
            </WhiteTooltip>
          ) : (
            compareButton
          );
        }

      },
    ],
    [selectedRows, theme, historyData]
  );

  const computedMinHeight = typeof height === 'number' ? Math.min(height, 200) : 200;
  const isCompareDisabled = selectedRows.length !== 2;

  const agGridStyles = useMemo(() => ({
    "& .ag-row": {
      cursor: "pointer !important",
    },
    "& .ag-cell": {
      cursor: "pointer !important",
    },
    "& .ag-cell-value": {
      cursor: "pointer !important",
    },
    "& .ag-cell-wrapper": {
      cursor: "pointer !important",
    },
    "& .ag-ellipsis": {
      cursor: "pointer !important",
    },
    "& span": {
      cursor: "pointer !important",
    },
    // Keep cursor default for action columns, but pointer on icons
    "& .ag-cell[col-id='showDiff']": {
      cursor: "pointer !important",
      "& *": {
        cursor: "pointer !important",
      },
      "& .MuiIconButton-root": {
        cursor: "pointer !important",
      },
      "& .MuiIconButton-root *": {
        cursor: "pointer !important",
      },
    },
    "& .ag-cell[col-id='addToCompare']": {
      cursor: "pointer !important",
      "& *": {
        cursor: "pointer !important",
      },
      "& .MuiIconButton-root": {
        cursor: "pointer !important",
      },
      "& .MuiIconButton-root *": {
        cursor: "pointer !important",
      },
    },
  }), []);

  //   () => ({
  //     padding: "0px",
  //     "& .ag-body-vertical-scroll": {
  //       width: "0px !important",
  //       minWidth: "0px !important",
  //     },
  //     "& .ag-body-vertical-scroll-viewport": {
  //       width: "0px !important",
  //       minWidth: "0px !important",
  //     },
  //     "& .ag-center-cols-clipper": {
  //       marginRight: 0,
  //     },
  //     "& .ag-center-cols-viewport": {
  //       marginRight: 0,
  //     },
  //   }),
  //   []
  // );


  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#ffffff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '16px',
            color: theme.palette.text.primary,
          }}
        >
          History
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InfoIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
              {selectedRows.length} out of 2 Added in Compare Selections
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={isCompareDisabled}
            onClick={handleCompareClick}
            sx={{
              fontWeight: 700, // bold
              color: isCompareDisabled ? "#9DA4AE" : "#3A2ADA", // custom text color
              borderColor: "#3A2ADA", // optional: match border
              "&.Mui-disabled": {
                borderColor: "#E0E3EB",
              },
            }}
          >
            Compare
          </Button>
        </Box>
      </Box>
      <Box sx={{ height: height ?? '320px', minHeight: computedMinHeight, overflow: 'hidden' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <SkeletonLoader count={13} styles={{ height: 40, width: "100%" }} />
          </Box>
        ) : (
          <AgGridTable
            columnDefs={columnDefs}
            data={historyData}
            height="100%"
            pagination={true}
            paginationPageSize={effectivePageSize}
            showAppsIcon={false}
            styles={agGridStyles}
            gridRef={gridRef}
            onRowClick={(rowData: HistoryTableRow) => {
              // Open summary drawer when row is clicked
              handleOpenSummary(rowData);
            }}
            gridOptions={{
              onGridReady: (event: any) => {
                // Set initial page size to 20 when grid is ready
                // Check if the method exists (AG Grid API compatibility)
                if (effectivePageSize && event.api?.paginationSetPageSize) {
                  event.api.paginationSetPageSize(effectivePageSize);
                }
              },
            }}
          />
        )}
      </Box>
      <ViewSummaryDrawer
        open={isSummaryDrawerOpen}
        onClose={handleCloseSummary}
        summaryData={summaryData}
        isLoading={summaryDataLoading}
        vendor={vendor}
      />
    </Box>
  );
};

export default HistoryTable;

